import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Filter as FilterIcon, X } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import FilterPopup from 'shared/components/filters/FilterPopup';
import PropertyPickerPopover from 'shared/components/filters/PropertyPickerPopover';
import InlineFilterChip from 'shared/components/filters/InlineFilterChip';
import { defaultOperatorForType } from 'shared/components/filters/FilterValueEditor';
import buildModularFiltersUtil from 'shared/utils/filters/buildModularFilters';
import Icon from 'shared/components/Icon';
import EvalChips from 'shared/components/filters/evals/EvalChips';
import { primary as PRIMARY } from 'shared/theme/trodoColors';
import {
  AddControlButton,
  QUERY_PANEL_CLASS,
  QUERY_SECTION_BODY_CLASS,
  QUERY_SECTION_DIVIDER_CLASS,
  QUERY_SECTION_HEADER_CLASS,
  QUERY_SECTION_LABEL_CLASS,
} from 'shared/components/filters/queryBuilderChrome';

let _uid = 0;
const newId = () => `f${++_uid}-${Math.random().toString(36).slice(2, 7)}`;

/* ------------------------------------------------------------------ */
/* AgentRow                                                            */
/* ------------------------------------------------------------------ */
function AgentRow({
  agent,
  scopedFilters,
  teamId,
  onAddFilter,
  onUpdateFilter,
  onRemoveAgent,
  onRemoveFilter,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const filterBtnRef = useRef(null);
  const myFilters = scopedFilters.filter((f) => f.agentId === agent.id);

  const handlePick = (picked) => {
    const op = defaultOperatorForType(picked.dataType);
    const id = newId();
    onAddFilter({
      ...picked,
      operator: op,
      value: picked.dataType === 'string' && op === 'is' ? [] : '',
      agentId: agent.id,
      id,
      _justAdded: true,
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* The agent as one tag: its name, a filter for it, and remove, together. */}
        <div className="inline-flex h-8 items-center gap-1 rounded-lg border border-[color:var(--border-default)] bg-surface pl-2.5 pr-1">
          <Icon name="agent" size={14} color={PRIMARY} />
          <span className="ml-0.5 mr-1 text-[13px] font-medium text-[color:var(--text-primary)]">{agent.agentName}</span>
          <button
            ref={filterBtnRef}
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex h-6 w-6 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            title="Filter by this agent"
            aria-label="Filter by this agent"
          >
            <FilterIcon size={13} />
          </button>
          <button
            type="button"
            onClick={onRemoveAgent}
            className="flex h-6 w-6 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            title="Remove agent"
            aria-label="Remove agent"
          >
            <X size={13} />
          </button>
        </div>
      </div>

      {myFilters.length > 0 ? (
        <div className="space-y-2 pl-3">
          {myFilters.map((f, idx) => (
            <InlineFilterChip
              key={f.id}
              filter={f}
              isFirst={idx === 0}
              teamId={teamId}
              agentName={agent.agentName}
              onUpdate={(updates) => onUpdateFilter(f.id, updates)}
              onRemove={() => onRemoveFilter(f.id)}
            />
          ))}
        </div>
      ) : null}

      <PropertyPickerPopover
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        anchorRef={filterBtnRef}
        teamId={teamId}
        scope="agent"
        agentName={agent.agentName}
        onPick={handlePick}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* AgentRunsQueryBuilder                                                */
/* ------------------------------------------------------------------ */
export default function AgentRunsQueryBuilder({
  teamId,
  selectedAgents,
  onSelectedAgentsChange,
  agentScopedFilters,
  onAgentScopedFiltersChange,
  globalFilters,
  onGlobalFiltersChange,
  evalLines = [],
  onEvalLinesChange = null,
  shown = true,
}) {
  const totalActive = selectedAgents.length + agentScopedFilters.length + globalFilters.length;

  // Agent picker state.
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [agentList, setAgentList] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const addAgentBtnRef = useRef(null);

  // Global filter property picker state.
  const [globalPickerOpen, setGlobalPickerOpen] = useState(false);
  const addGlobalFilterBtnRef = useRef(null);

  useEffect(() => {
    if (!agentPickerOpen || !teamId) return undefined;
    const ac = new AbortController();
    setAgentLoading(true);
    axiosInstance
      .get('/agent-runs/agents', { params: { team_id: teamId }, signal: ac.signal })
      .then((res) => setAgentList(res.data?.agents || []))
      .catch(() => setAgentList([]))
      .finally(() => setAgentLoading(false));
    return () => ac.abort();
  }, [agentPickerOpen, teamId]);

  const availableAgents = useMemo(
    () =>
      (agentList || [])
        .filter((a) => !selectedAgents.find((s) => s.agentName === a.agent_name))
        .map((a) => ({
          id: a.agent_name,
          eventName: a.agent_name,
          displayName: a.agent_name,
          description: `${(a.run_count || 0).toLocaleString()} run${a.run_count === 1 ? '' : 's'}` +
            (a.last_seen_at ? ` · last seen ${new Date(a.last_seen_at).toLocaleDateString()}` : ''),
          eventType: 'agent',
          tags: [],
        })),
    [agentList, selectedAgents],
  );

  const handleAgentSelect = (event) => {
    onSelectedAgentsChange([...selectedAgents, { id: newId(), agentName: event.eventName }]);
    setAgentPickerOpen(false);
  };

  const removeAgent = (id) => {
    const removed = selectedAgents.find((a) => a.id === id);
    onSelectedAgentsChange(selectedAgents.filter((a) => a.id !== id));
    if (removed) {
      onAgentScopedFiltersChange(
        agentScopedFilters.filter((f) => f.agentName !== removed.agentName),
      );
    }
  };

  const addAgentScopedFilter = (filter) => {
    onAgentScopedFiltersChange((prev) => [...prev, filter]);
  };
  const updateAgentScopedFilter = (id, updates) => {
    onAgentScopedFiltersChange((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    );
  };
  const removeAgentScopedFilter = (id) => {
    onAgentScopedFiltersChange((prev) => prev.filter((f) => f.id !== id));
  };

  const handleGlobalPick = (picked) => {
    const op = defaultOperatorForType(picked.dataType);
    const id = newId();
    const nextFilter = {
      ...picked,
      operator: op,
      value: picked.dataType === 'string' && op === 'is' ? [] : '',
      id,
      _justAdded: true,
    };
    onGlobalFiltersChange((prev) => [...prev, nextFilter]);
  };
  const updateGlobalFilter = (id, updates) => {
    onGlobalFiltersChange((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    );
  };
  const removeGlobalFilter = (id) => {
    onGlobalFiltersChange((prev) => prev.filter((f) => f.id !== id));
  };

  if (!shown) return null;

  return (
    <div className="flex flex-col gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className={QUERY_PANEL_CLASS}>
        {/* AGENTS section */}
        <div className="bg-transparent">
          <div className={QUERY_SECTION_HEADER_CLASS}>
            <h3 className={QUERY_SECTION_LABEL_CLASS}>AGENTS</h3>
          </div>

          <div className={QUERY_SECTION_BODY_CLASS}>
            {selectedAgents.map((a) => (
              <AgentRow
                key={a.id}
                agent={a}
                teamId={teamId}
                scopedFilters={agentScopedFilters}
                onAddFilter={addAgentScopedFilter}
                onUpdateFilter={updateAgentScopedFilter}
                onRemoveAgent={() => removeAgent(a.id)}
                onRemoveFilter={removeAgentScopedFilter}
              />
            ))}

            <div>
              <AddControlButton buttonRef={addAgentBtnRef} onClick={() => setAgentPickerOpen(true)} />
            </div>
          </div>
        </div>

        <div className={QUERY_SECTION_DIVIDER_CLASS} />

        {/* FILTERS section */}
        <div className="bg-transparent">
          <div className={QUERY_SECTION_HEADER_CLASS}>
            <h3 className={QUERY_SECTION_LABEL_CLASS}>FILTERS</h3>
          </div>

          <div className={QUERY_SECTION_BODY_CLASS}>
            {globalFilters.map((f, idx) => (
              <InlineFilterChip
                key={f.id}
                filter={f}
                isFirst={idx === 0}
                teamId={teamId}
                onUpdate={(updates) => updateGlobalFilter(f.id, updates)}
                onRemove={() => removeGlobalFilter(f.id)}
              />
            ))}

            {/* Eval conditions are filters too: picked from the Evals tab, shown as chips here. */}
            {onEvalLinesChange ? <EvalChips teamId={teamId} lines={evalLines} onChange={onEvalLinesChange} after={globalFilters.length} /> : null}

            <div>
              <AddControlButton buttonRef={addGlobalFilterBtnRef} onClick={() => setGlobalPickerOpen(true)} />
            </div>
          </div>
        </div>

      </div>

      {/* Agent selection popup: shared FilterPopup chrome. Tab is "Agents". */}
      <FilterPopup
        isOpen={agentPickerOpen}
        onClose={() => setAgentPickerOpen(false)}
        triggerRef={addAgentBtnRef}
        navItems={[{ id: 'all', label: 'Agents', NavIcon: AgentNavIcon }]}
        filterConfigs={{ all: { fields: [], operators: {}, defaultOperators: {} } }}
        filters={[]}
        onFiltersChange={() => {}}
        getFieldOptions={() => []}
        onEventSelect={handleAgentSelect}
        availableEvents={availableAgents}
        loadingEvents={agentLoading}
        searchPlaceholder="Search agents…"
        itemNoun="agents"
      />

      {/* Global filter property picker */}
      <PropertyPickerPopover
        open={globalPickerOpen}
        onClose={() => setGlobalPickerOpen(false)}
        anchorRef={addGlobalFilterBtnRef}
        teamId={teamId}
        scope="global"
        onPick={handleGlobalPick}
        onPickEval={onEvalLinesChange ? (line) => onEvalLinesChange([...(evalLines || []), line]) : null}
      />
    </div>
  );
}

function AgentNavIcon({ size = 16, className = '', style = {} }) {
  return <Icon name="agent" size={size} className={className} style={style} />;
}

/* ------------------------------------------------------------------ */
/* Helpers used by the parent page (re-exported from shared)           */
/* ------------------------------------------------------------------ */
export const buildModularFilters = buildModularFiltersUtil;
